import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDemo } from "@/store/DemoContext";
import { getLiveFixtures } from "@/data";
import { userResult } from "@/lib/results";
import { cumulativeSeries, scoreMeta } from "@/lib/scoring";
import { fmtNumber, fmtScore } from "@/lib/format";
import { ENTITY } from "@/data/constants";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer, ReferenceLine,
} from "recharts";
import { TrendingDown, CalendarDays } from "lucide-react";

const C = { you: ENTITY.you.color, coach: ENTITY.coach.color, ai: ENTITY.ai.color };

const CHART_MARGIN = { top: 8, right: 12, bottom: 4, left: -8 };
const CHART_DIMS = { width: "100%", height: 300 };
const TOOLTIP_STYLE = { background: "#111", border: "1px solid #333", borderRadius: 8, fontSize: 12 };
const DOT_AI = { r: 2, fill: C.ai };
const DOT_COACH = { r: 2, fill: C.coach };
const DOT_YOU = { r: 3, fill: C.you };

export default function SeasonView() {
  const { t } = useTranslation();
  const { dataset, state } = useDemo();
  const { language, scoringMode } = state;
  const profile = dataset.profiles[state.profileId];
  const meta = scoreMeta(scoringMode);

  const history = useMemo(() => {
    const h = [...profile.history];
    // append live matchday if user submitted & settled
    const live = getLiveFixtures("elite")[0];
    const sub = state.submissions[live.id];
    const liveState = state.fixtureStates[live.id] || live.defaultState;
    if (sub && liveState === "SETTLED") {
      const r = userResult(live, sub);
      h.push({ matchday: dataset.liveMatchday, lgd: r.lgd, coachLGD: live.coachXI.lgd, aiLGD: live.aiXI.lgd, actualGD: live.actualGD, live: true });
    }
    return h;
  }, [profile.history, state.submissions, state.fixtureStates, dataset.liveMatchday]);

  if (history.length === 0) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-20 text-center" data-testid="season-empty">
        <div className="w-14 h-14 rounded-2xl bg-neutral-900 border border-neutral-800 grid place-items-center mx-auto mb-4"><CalendarDays className="text-neutral-500" /></div>
        <h2 className="font-display text-2xl font-bold uppercase">{t("season.emptyTitle")}</h2>
        <p className="text-neutral-400 mt-2">{t("season.emptyBody")}</p>
      </div>
    );
  }

  const youCum = cumulativeSeries(scoringMode, history, "lgd");
  const coachCum = cumulativeSeries(scoringMode, history, "coach");
  const aiCum = cumulativeSeries(scoringMode, history, "ai");
  const chartData = history.map((h, i) => ({
    md: h.matchday,
    you: youCum[i], coach: coachCum[i], ai: aiCum[i],
  }));

  const beatCoach = history.filter((h) => h.lgd < h.coachLGD).length;
  const beatAi = history.filter((h) => h.lgd < h.aiLGD).length;
  const cumulative = youCum[youCum.length - 1];
  const best = history.reduce((a, b) => (b.lgd < a.lgd ? b : a));
  const worst = history.reduce((a, b) => (b.lgd > a.lgd ? b : a));
  const eliteClubs = dataset.leagues[0].clubIds;

  return (
    <div className="max-w-6xl mx-auto px-3 sm:px-6 py-5 space-y-5">
      <h1 className="font-display text-3xl font-bold uppercase tracking-wide">{t("season.title")}</h1>

      {/* tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 stagger" data-testid="season-tiles">
        <Tile label={t("season.cumulative")} value={fmtScore(scoringMode, cumulative, language)} color="#b6ff2e" />
        <Tile label={t("season.played")} value={history.length} />
        <Tile label={t("season.beatCoachTimes")} value={`${beatCoach}`} color="#ffb547" />
        <Tile label={t("season.beatAiTimes")} value={`${beatAi}`} color="#3fd7e5" />
        <Tile label={t("season.best")} value={fmtNumber(best.lgd, language, 2)} />
        <Tile label={t("season.worst")} value={fmtNumber(worst.lgd, language, 2)} />
      </div>

      {/* chart */}
      <div className="rounded-2xl border border-neutral-800 bg-neutral-900/40 p-4">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <span className="font-display uppercase text-sm tracking-wide">{t("season.chartTitle")}</span>
          <div className="flex items-center gap-3">
            {["you", "coach", "ai"].map((k) => (
              <span key={k} className="flex items-center gap-1.5 text-xs text-neutral-400">
                <span className="w-3 h-0.5 rounded" style={{ background: C[k] }} /> {t(`common.${k}`)}
              </span>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-2 mb-2 text-[11px]">
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#b6ff2e]/10 text-[#b6ff2e] border border-[#b6ff2e]/30">
            <TrendingDown size={12} /> {meta.betterIsLower ? t("season.axisNote") : t("common.higherIsBetter")}
          </span>
        </div>
        <div style={CHART_DIMS} data-testid="season-chart">
          <ResponsiveContainer>
            <LineChart data={chartData} margin={CHART_MARGIN}>
              <CartesianGrid stroke="#222" strokeDasharray="3 3" />
              <XAxis dataKey="md" stroke="#666" fontSize={11} tickLine={false}
                label={{ value: t("common.matchday"), position: "insideBottom", offset: -2, fill: "#555", fontSize: 10 }} />
              <YAxis stroke="#666" fontSize={11} reversed={meta.betterIsLower} tickLine={false}
                tickFormatter={(v) => fmtNumber(v, language, 1)} />
              {meta.betterIsLower && <ReferenceLine y={0} stroke="#333" />}
              {!meta.betterIsLower && <ReferenceLine y={0} stroke="#555" strokeDasharray="4 4" />}
              <RTooltip contentStyle={TOOLTIP_STYLE}
                formatter={(v, n) => [fmtNumber(v, language, 2), t(`common.${n}`)]} labelFormatter={(l) => `${t("common.matchday")} ${l}`} />
              <Line type="monotone" dataKey="ai" stroke={C.ai} strokeWidth={2} isAnimationActive={false} dot={DOT_AI} />
              <Line type="monotone" dataKey="coach" stroke={C.coach} strokeWidth={2} isAnimationActive={false} dot={DOT_COACH} />
              <Line type="monotone" dataKey="you" stroke={C.you} strokeWidth={2.5} isAnimationActive={false} dot={DOT_YOU} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <p className="text-[11px] text-neutral-500 mt-1">
          {meta.betterIsLower ? "Y-axis inverted so a lower cumulative cost sits higher — the top line is winning." : "Running credit balance — above zero is a net gain of goals."}
        </p>
      </div>

      {/* form strip */}
      <div className="rounded-2xl border border-neutral-800 bg-neutral-900/40 p-4">
        <div className="font-display uppercase text-sm tracking-wide mb-2">{t("season.formTitle")}</div>
        <div className="flex gap-1.5" data-testid="form-strip">
          {history.slice(-6).map((h) => {
            const win = h.lgd < h.coachLGD;
            return (
              <div key={h.matchday} className="w-8 h-8 rounded-lg grid place-items-center font-display font-bold text-xs"
                style={{ background: win ? "#b6ff2e" : "#3a2020", color: win ? "#0a0a0a" : "#ff8f8f" }}>
                {win ? "W" : "L"}
              </div>
            );
          })}
        </div>
      </div>

      {/* table */}
      <div className="rounded-2xl border border-neutral-800 overflow-hidden">
        <div className="px-4 py-2.5 border-b border-neutral-800 bg-neutral-900/60 font-display uppercase text-sm tracking-wide">{t("season.table")}</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="season-table">
            <thead>
              <tr className="text-[11px] uppercase text-neutral-500 border-b border-neutral-800">
                <th className="text-left px-4 py-2 font-display">{t("common.matchday")}</th>
                <th className="text-left px-2 py-2 font-display">{t("season.opponent")}</th>
                <th className="text-right px-2 py-2 font-display" style={{ color: C.you }}>{t("season.yourLgd")}</th>
                <th className="text-right px-2 py-2 font-display" style={{ color: C.coach }}>{t("season.coachLgd")}</th>
                <th className="text-right px-2 py-2 font-display" style={{ color: C.ai }}>{t("season.aiLgd")}</th>
                <th className="text-right px-4 py-2 font-display">{t("season.verdict")}</th>
              </tr>
            </thead>
            <tbody>
              {[...history].reverse().map((h) => {
                const win = h.lgd < h.coachLGD;
                const opp = dataset.clubsById[eliteClubs[(h.matchday * 5) % eliteClubs.length]];
                return (
                  <tr key={h.matchday} className={`border-b border-neutral-900 last:border-0 ${h.live ? "bg-[#b6ff2e]/5" : ""}`}>
                    <td className="px-4 py-2 font-display font-bold">{h.matchday}{h.live && <span className="text-[#b6ff2e] text-[10px] ml-1">LIVE</span>}</td>
                    <td className="px-2 py-2 text-neutral-400">{opp?.code}</td>
                    <td className="px-2 py-2 text-right tabular" style={{ color: C.you }}>{fmtNumber(h.lgd, language, 2)}</td>
                    <td className="px-2 py-2 text-right tabular text-neutral-400">{fmtNumber(h.coachLGD, language, 2)}</td>
                    <td className="px-2 py-2 text-right tabular text-neutral-400">{fmtNumber(h.aiLGD, language, 2)}</td>
                    <td className="px-4 py-2 text-right">
                      <span className="text-[10px] px-2 py-0.5 rounded-full font-display uppercase" style={{ background: win ? "#b6ff2e22" : "#ff6b6b22", color: win ? "#b6ff2e" : "#ff8f8f" }}>
                        {win ? t("season.beatCoach") : t("season.lostToCoach")}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Tile({ label, value, color = "#e5e5e5" }) {
  return (
    <div className="rounded-xl border border-neutral-800 bg-neutral-900/50 p-3">
      <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-display">{label}</div>
      <div className="font-display text-2xl font-bold tabular mt-1" style={{ color }}>{value}</div>
    </div>
  );
}
